/**
 * Feldtyp-Register und der laute Rückfall — die Absicherung dafür, dass ein
 * `FieldType` nie wieder nur auf einer Seite ankommt.
 *
 * **Warum es diese Datei gibt.** Das Formular hat zwei völlig getrennte
 * Renderer: `apps/web/src/components/risikoanalyseStep/QuestionField.tsx`
 * (Beratermodus und öffentliches Kundenformular) und
 * `packages/forms-react/src/FormFieldRenderer.tsx` samt seinem Adapter
 * `apps/portal-web/src/forms/FormField.tsx` (Kundenportal). Beide sind
 * if-Ketten. Wer bis hierher einen Feldtyp ergänzte und nur einen der beiden
 * anfasste, bekam einen grünen type-check, einen grünen Build — und auf der
 * vergessenen Seite ein nacktes Textfeld (im Portal: drei Kontakt-Eingaben,
 * weil dessen Rückfall der ContactPicker-Adapter ist). Der Kunde tippt dann
 * Text in ein Feld, das eine Datei erwartet, und niemand merkt es.
 *
 * **Wie es jetzt abgesichert ist — drei Teile, die zusammengehören:**
 *
 * 1. {@link FIELD_TYPES} ist die EINE Liste aller Typen. Sie entsteht aus
 *    einem `Record<FieldType, true>`; ein neuer Typ in der Union ohne Eintrag
 *    hier ist ein Compile-Fehler in diesem Paket.
 * 2. Jeder Renderer exportiert die Typen, die er tatsächlich behandelt, und je
 *    ein Test in apps/web, packages/forms-react und apps/portal-web prüft diese
 *    Menge gegen {@link FIELD_TYPES}. Ein einseitig umgesetzter Typ macht die
 *    Test-Suite der VERGESSENEN Seite rot.
 * 3. Bleibt trotzdem etwas durch (ein Schema aus einem neueren Deploy trifft
 *    auf ein älteres Frontend), fällt der Renderer nicht mehr still auf Text
 *    zurück, sondern auf {@link UNKNOWN_FIELD_TYPE_ATTR}: sichtbarer Hinweis
 *    im Formular statt stiller Falschanzeige, `console.error` für den Betrieb,
 *    gespeicherter Wert bleibt unangetastet — und kein Absturz.
 */
import { DOKUMENT_STATUS_WERTE, } from "./types.js";
/**
 * Vollzählig, weil `Record<FieldType, …>` jeden Schlüssel verlangt: ein
 * fehlender Typ ist hier ein Compile-Fehler, ein erfundener ebenfalls.
 */
const FELDTYP_REGISTER = {
    text: true,
    textarea: true,
    number: true,
    date: true,
    yesno: true,
    select: true,
    checkboxes: true,
    iconCards: true,
    personEntries: true,
    verantEntries: true,
    firmaEntries: true,
    contactPicker: true,
    documents: true,
    dokumentStatus: true,
    branchen: true,
};
/** Alle Feldtypen — Prüfmass für jede Renderer-Abdeckung. */
export const FIELD_TYPES = Object.freeze(Object.keys(FELDTYP_REGISTER));
/** Kennt das Paket diesen Typ? (Antwortsätze und Schemata sind zur Laufzeit `unknown`.) */
export const isFieldType = (value) => typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(FELDTYP_REGISTER, value);
// ── Dokument-Referenzen ────────────────────────────────────────────────
/**
 * Ist der Wert eine brauchbare Dokument-Referenz?
 *
 * Bewusst tolerant bei allem Optionalen und streng nur bei dem, was ohne
 * Bedeutung wäre: ohne ganzzahlige `documentId > 0` gibt es nichts zum
 * Herunterladen, ohne `fileName` nichts anzuzeigen. Zusätzliche Felder stören
 * nicht — der Server darf die Referenz erweitern, ohne ältere Clients zu
 * brechen.
 */
export const isDocumentRef = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return false;
    const ref = value;
    return (typeof ref.documentId === "number" &&
        Number.isInteger(ref.documentId) &&
        ref.documentId > 0 &&
        typeof ref.fileName === "string" &&
        ref.fileName.trim() !== "");
};
/**
 * Liest die Dateiliste aus einem rohen Antwortwert.
 *
 * Wirft nie und wirft nichts weg, was noch anzeigbar ist: Fremdwerte unter
 * einem `documents`-Key (ein Textstand aus der Zeit vor dem Slot, ein halb
 * geschriebenes Objekt) liefern eine leere Liste, statt den Renderer zu
 * zerlegen. Was fachlich davon zu halten ist, entscheidet
 * `validateFormAnswers` über `invalidValues` — nicht die Anzeige.
 */
export const documentRefs = (value) => Array.isArray(value) ? value.filter(isDocumentRef) : [];
// ── Dokument-Status ────────────────────────────────────────────────────
const DOKUMENT_STATUS_CODES = new Set(DOKUMENT_STATUS_WERTE.map((w) => w.value));
/** Kennt das Paket diesen Bearbeitungsstand? */
export const isDokumentStatusCode = (value) => typeof value === "string" && DOKUMENT_STATUS_CODES.has(value);
/**
 * Liest den Stand eines Vollmacht-Papiers aus einem rohen Antwortwert.
 *
 * Gleiche Haltung wie {@link documentRefs}: nie werfen, Unbrauchbares still
 * weglassen. Ein unbekannter Status-Code (Schema aus einem neueren Deploy,
 * von Hand geänderter Entwurf) liefert `undefined` statt einer Auswahl, die
 * es in der Liste nicht gibt — die Zeile steht dann auf „noch nichts
 * eingetragen" und lässt sich normal setzen.
 */
export const dokumentStatusWert = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return {};
    const roh = value;
    const datum = typeof roh.datum === "string" && roh.datum.trim() !== "" ? roh.datum : undefined;
    return {
        ...(isDokumentStatusCode(roh.status) ? { status: roh.status } : {}),
        ...(datum ? { datum } : {}),
    };
};
/** Beschriftung eines Standes („liegt vor"); unbekannt ⇒ leerer String. */
export const dokumentStatusLabel = (status) => DOKUMENT_STATUS_WERTE.find((w) => w.value === status)?.label ?? "";
/**
 * Dateigrösse in der Schreibweise des Formulars („1,2 MB"). Beide Renderer
 * zeigen dieselbe Zahl — sonst hiesse dieselbe Datei im Portal anders als im
 * Beratermodus. Unbekannte Grösse ⇒ leerer String (der Aufrufer lässt die
 * Angabe dann weg).
 */
export const formatDocumentSize = (bytes) => {
    if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0)
        return "";
    if (bytes < 1024)
        return `${Math.round(bytes)} B`;
    const kb = bytes / 1024;
    if (kb < 1024)
        return `${kb.toFixed(kb < 10 ? 1 : 0).replace(".", ",")} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(mb < 10 ? 1 : 0).replace(".", ",")} MB`;
};
/**
 * Upload-Datum in der Schreibweise des Formulars („18.08.2026"). Unbekanntes
 * oder unlesbares Datum ⇒ leerer String.
 */
export const formatDocumentDate = (iso) => {
    if (!iso)
        return "";
    const wert = new Date(iso);
    return Number.isNaN(wert.getTime())
        ? ""
        : wert.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};
/** Herkunftsmarke einer vom Kunden hochgeladenen Datei. */
export const DOCUMENT_HERKUNFT_KUNDE = "vom Kunden";
/** Zerlegt eine Referenz in genau die Angaben, die jede Anzeige zeigen muss. */
export const documentRefAnzeige = (ref) => ({
    name: ref.fileName,
    meta: [formatDocumentSize(ref.sizeBytes), formatDocumentDate(ref.uploadedAt)]
        .filter(Boolean)
        .join(" · "),
    notiz: typeof ref.note === "string" ? ref.note.trim() : "",
    herkunft: ref.vomKunden ? DOCUMENT_HERKUNFT_KUNDE : "",
});
/**
 * Der Satz, den ein Datei-Slot zeigt, solange an ihm nichts hochgeladen
 * werden kann.
 *
 * Er steht hier und nicht in einem der Renderer, weil ihn BEIDE brauchen und
 * er sonst in zwei Fassungen auseinanderliefe. Bis zu diesem Stand hatte ihn
 * nur das Portal: Im öffentlichen Token-Formular (apps/web) las der Kunde die
 * Auflagen des Slots („PDF, JPG · höchstens 5 Dateien"), fand aber weder einen
 * Knopf noch eine Erklärung, wie er sie erfüllen soll.
 *
 * Bewusst OHNE Anrede: Denselben Slot sieht auch der Berater in der
 * Auswertung und im Prüf-Modal (dort gilt ebenfalls `readonly`) — „Ihr
 * Berater fordert das bei Ihnen an" schickte ihn zu sich selbst.
 */
export const DOKUMENTE_NUR_ANSICHT_HINWEIS = "Unterlagen können hier nicht hochgeladen werden — der Austausch läuft separat über den Berater.";
// ── Lauter Rückfall ────────────────────────────────────────────────────
/**
 * Markierung am Rückfall-Element. Sie steht im DOM, damit die Tests aller drei
 * Pakete dasselbe suchen können: „rendert dieser Renderer für IRGENDEINEN
 * bekannten Feldtyp den Rückfall?"
 */
export const UNKNOWN_FIELD_TYPE_ATTR = "data-unbekannter-feldtyp";
/**
 * Was Berater oder Kunde zu sehen bekommt. Kein Fachchinesisch und keine
 * Schuldzuweisung, aber unmissverständlich: Das Feld ist hier nicht bedienbar,
 * und die vorhandene Antwort ist nicht verloren.
 */
export const unknownFieldTypeMessage = (field) => `Dieses Feld lässt sich hier nicht anzeigen (Feldtyp „${String(field.type)}"). ` +
    `Bitte wenden Sie sich an Ihren Berater — Ihre übrigen Angaben bleiben erhalten.`;
/** Schon gemeldete Kombinationen; die Konsole soll nicht bei jedem Rendern volllaufen. */
const gemeldet = new Set();
/**
 * Meldet den Rückfall EINMAL je Renderer und Feldtyp in die Konsole.
 *
 * `console.error` mit festem Präfix, damit es im Produktivbetrieb auffindbar
 * ist (Browser-Konsole, Session-Aufzeichnung, Fehlerreport) — geworfen wird
 * bewusst nichts: ein Formular mit einem unbekannten Feld muss weiter
 * ausfüllbar und absendbar bleiben.
 */
export const reportUnknownFieldType = (renderer, field) => {
    const marke = `${renderer}:${String(field.type)}`;
    if (gemeldet.has(marke))
        return;
    gemeldet.add(marke);
    // eslint-disable-next-line no-console
    console.error(`[forms] Unbekannter Feldtyp „${String(field.type)}" in ${renderer} ` +
        `(Feld „${field.key}"). Der Feldtyp ist in @kmvs/forms-core definiert, ` +
        `aber in diesem Renderer nicht umgesetzt — siehe FIELD_TYPES.`);
};
/** Nur für Tests: die Merkliste der bereits gemeldeten Feldtypen leeren. */
export const resetUnknownFieldTypeReports = () => gemeldet.clear();
// ── Branche und Spezialisierung ───────────────────────────────────────
/**
 * Ist der Wert eine brauchbare Branchen-Zuordnung?
 *
 * Streng nur bei dem, was ohne Bedeutung wäre: Ohne ganzzahlige
 * `brancheClassId > 0` gibt es kein Katalogblatt, auf das sie zeigt. Der
 * `label` darf fehlen — er ist Anzeige, nicht Inhalt.
 */
export const isBrancheWahl = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return false;
    const v = value;
    return Number.isInteger(v.brancheClassId) && v.brancheClassId > 0;
};
/** Die brauchbaren Zuordnungen eines Antwortwerts, in ihrer Reihenfolge. */
export const brancheWahlListe = (value) => Array.isArray(value) ? value.filter(isBrancheWahl) : [];
/**
 * Anzeigetext einer Zuordnung.
 *
 * Fällt auf die Id zurück, wenn kein `label` mitgespeichert wurde — eine leere
 * Zeile verschwiege, dass da etwas steht.
 */
export const brancheWahlLabel = (wahl) => (wahl.label ?? "").trim() || `Branche ${wahl.brancheClassId}`;
